import { useIsOverQuota } from '@qse/stores'
import React, { useEffect, useRef, useState } from 'react'

import { CreateNewFolder, NewFileUpload, NewFolderUpload } from '../icons'

export type NewContextMenuAction = 'new-folder' | 'file-upload' | 'folder-upload'

interface NewContextMenuProps {
  isOpen: boolean
  position: { x: number; y: number }
  onClose: () => void
  onRequestClose?: () => void
  onAction: (action: NewContextMenuAction) => void
}

export const NewContextMenu: React.FC<NewContextMenuProps> = ({
  isOpen,
  position,
  onAction,
  onRequestClose,
}) => {
  const isOverQuota = useIsOverQuota()
  const contextMenuRef = useRef<HTMLDivElement>(null)
  const menuItemClickedRef = useRef(false)
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 })
  const [isPositioned, setIsPositioned] = useState(false)

  // Calculate optimal menu position
  useEffect(() => {
    if (isOpen) {
      // Set initial position immediately to prevent flash
      let x = position.x + 5
      let y = position.y + 5

      // Basic overflow protection for initial position
      x = Math.max(10, Math.min(x, window.innerWidth - 210)) // 200px width + 10px margin
      y = Math.max(10, Math.min(y, window.innerHeight - 150)) // Approximate height + 10px margin

      setMenuPosition({ x, y })
      setIsPositioned(true)

      // Then recalculate with actual dimensions
      requestAnimationFrame(() => {
        if (contextMenuRef.current) {
          // Get actual menu dimensions after render
          const menuRect = contextMenuRef.current.getBoundingClientRect()
          const menuWidth = menuRect.width
          const menuHeight = menuRect.height

          // Start with cursor position
          x = position.x
          y = position.y

          // Add small offset to prevent menu from appearing directly under cursor
          x += 5
          y += 5

          // Adjust horizontal position if menu would overflow right edge
          if (x + menuWidth > window.innerWidth) {
            x = window.innerWidth - menuWidth - 10
          }

          // Adjust vertical position if menu would overflow bottom edge
          if (y + menuHeight > window.innerHeight) {
            y = window.innerHeight - menuHeight - 10
          }

          // Ensure menu doesn't go off-screen to the left or top
          x = Math.max(10, x)
          y = Math.max(10, y)

          setMenuPosition({ x, y })
        }
      })
    } else {
      setIsPositioned(false)
    }
  }, [isOpen, position.x, position.y])

  // Recalculate position after menu items are rendered
  useEffect(() => {
    if (isOpen && contextMenuRef.current) {
      // Use a small delay to ensure menu items are fully rendered
      const timeoutId = setTimeout(() => {
        const menuRect = contextMenuRef.current?.getBoundingClientRect()

        if (menuRect) {
          const menuWidth = menuRect.width
          const menuHeight = menuRect.height

          let x = menuPosition.x
          let y = menuPosition.y

          // Adjust horizontal position if menu would overflow right edge
          if (x + menuWidth > window.innerWidth) {
            x = window.innerWidth - menuWidth - 10
          }

          // Adjust vertical position if menu would overflow bottom edge
          if (y + menuHeight > window.innerHeight) {
            y = window.innerHeight - menuHeight - 10
          }

          // Ensure menu doesn't go off-screen to the left or top
          x = Math.max(10, x)
          y = Math.max(10, y)

          setMenuPosition({ x, y })
        }
      }, 50) // Small delay to ensure rendering is complete

      return () => clearTimeout(timeoutId)
    }
  }, [isOpen, menuPosition.x, menuPosition.y])

  // Add window resize handler to reposition menu if needed
  useEffect(() => {
    const handleResize = () => {
      if (isOpen && contextMenuRef.current) {
        const menuRect = contextMenuRef.current.getBoundingClientRect()
        const menuWidth = menuRect.width
        const menuHeight = menuRect.height

        let x = menuPosition.x
        let y = menuPosition.y

        // Ensure menu is still visible after resize
        if (x + menuWidth > window.innerWidth) {
          x = window.innerWidth - menuWidth - 10
        }
        if (y + menuHeight > window.innerHeight) {
          y = window.innerHeight - menuHeight - 10
        }

        x = Math.max(10, x)
        y = Math.max(10, y)

        setMenuPosition({ x, y })
      }
    }

    const handleScroll = () => {
      if (isOpen && typeof onRequestClose === 'function') {
        // Close menu on scroll to prevent positioning issues
        onRequestClose()
      }
    }

    if (isOpen) {
      window.addEventListener('resize', handleResize)
      window.addEventListener('scroll', handleScroll, true)

      return () => {
        window.removeEventListener('resize', handleResize)
        window.removeEventListener('scroll', handleScroll, true)
      }
    }
  }, [isOpen, menuPosition.x, menuPosition.y, onRequestClose])

  useEffect(() => {
    const preventScroll = (e: Event) => {
      e.preventDefault()
    }

    if (isOpen) {
      document.body.style.overflow = 'hidden'
      document.addEventListener('wheel', preventScroll, { passive: false })
      document.addEventListener('touchmove', preventScroll, { passive: false })
    } else {
      document.body.style.overflow = 'unset'
      document.removeEventListener('wheel', preventScroll)
      document.removeEventListener('touchmove', preventScroll)
    }

    return () => {
      document.body.style.overflow = 'unset'
      document.removeEventListener('wheel', preventScroll)
      document.removeEventListener('touchmove', preventScroll)
    }
  }, [isOpen])

  // Add click outside handler to auto-close context menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isOpen) return

      const target = event.target as Node

      // Check if click is inside the context menu
      if (contextMenuRef.current && contextMenuRef.current.contains(target)) {
        return
      }

      // Check if click is on a menu item specifically
      const isOnMenuItem =
        (target as Element).closest('[role="menuitem"]') ||
        (target as Element).closest('.context-menu-item') ||
        (target as Element).closest('.px-3') // Menu item styling class

      if (isOnMenuItem) {
        return
      }

      // Don't close if a menu item was just clicked
      if (menuItemClickedRef.current) {
        menuItemClickedRef.current = false

        return
      }

      // Close the menu for any click outside
      if (typeof onRequestClose === 'function') {
        onRequestClose()
      }
    }

    if (isOpen) {
      // Use a small delay to prevent immediate closing when opening the menu
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside, true) // Use capture phase
      }, 100)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('click', handleClickOutside)
      }
    }
  }, [isOpen, onRequestClose])

  // Add escape key handler
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        if (typeof onRequestClose === 'function') {
          onRequestClose()
        }
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onRequestClose])

  if (!isOpen || !isPositioned) return null

  const menuItems = [
    {
      label: 'New Folder',
      icon: CreateNewFolder,
      action: 'new-folder' as NewContextMenuAction,
    },
    {
      label: 'File Upload',
      icon: NewFileUpload,
      action: 'file-upload' as NewContextMenuAction,
    },
    {
      label: 'Folder Upload',
      icon: NewFolderUpload,
      action: 'folder-upload' as NewContextMenuAction,
    },
  ]

  return (
    <div
      ref={contextMenuRef}
      className="context-menu fixed bg-white dark:bg-[#343537] w-[200px] border border-gray-300 dark:border-[#707070] rounded-xl shadow-xl z-[9999] py-2"
      role="menu"
      style={{
        left: menuPosition.x,
        top: menuPosition.y,
      }}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => e.stopPropagation()}
    >
      {menuItems.map(({ label, icon: Icon, action }) => (
        <div
          key={action}
          role="menuitem"
          className={`px-3 sm:px-4 py-2 flex items-center gap-3 text-sm ${
            isOverQuota && (action === 'file-upload' || action === 'folder-upload')
              ? 'text-gray-500 dark:text-gray-500 cursor-not-allowed'
              : 'text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-[#282828]/50 cursor-pointer'
          }`}
          title={
            isOverQuota && (action === 'file-upload' || action === 'folder-upload')
              ? 'Storage quota exceeded. Delete files to free up space.'
              : undefined
          }
          onKeyDown={e => e.stopPropagation()}
          onClick={() => {
            if (isOverQuota && (action === 'file-upload' || action === 'folder-upload')) return
            menuItemClickedRef.current = true
            if (typeof onRequestClose === 'function') onRequestClose()
            onAction(action)
          }}
        >
          <Icon className="w-4 h-4 flex-shrink-0" />
          <span>{label}</span>
        </div>
      ))}
    </div>
  )
}
